import { useState, useEffect, useCallback } from 'react'
import { extractDynamicDecimalPlaces } from '@/utils/currency/extractDynamicDecimalPlaces'

interface Props {
  value?: string
  currencyCode?: string
  onChangeText?: (text: string) => void
  shouldMaskStablecoinAsFiat?: boolean
  maxLength?: number
}

/**
 * Hook for validating amount text input
 * Ported from React Native version with identical logic
 */
export function useValidatedValueAmountTextInput({
  value,
  currencyCode,
  onChangeText,
  shouldMaskStablecoinAsFiat = true,
  maxLength,
}: Props) {
  const [validatedValue, setValidatedValue] = useState(value || '0')

  // Helper function to format value with decimal limit
  const formatValueWithDecimalLimit = useCallback(
    (val: string): string => {
      if (!val || val === '0') return val

      const maxDecimals = extractDynamicDecimalPlaces({
        currencyCode,
        shouldMaskStablecoinAsFiat,
      })

      const hasDecimals = val.includes('.')
      if (hasDecimals) {
        const [integerPart, decimalPart] = val.split('.')
        if (decimalPart && decimalPart.length > maxDecimals) {
          return `${integerPart}.${decimalPart.slice(0, maxDecimals)}`
        }
      }

      return val
    },
    [currencyCode, shouldMaskStablecoinAsFiat],
  )

  useEffect(() => {
    if (
      (!!value && value === validatedValue) ||
      (value === '0' && validatedValue === '0.')
    )
      return

    // Apply decimal limit when value changes externally (e.g., when switching fiat mode)
    const formattedValue = formatValueWithDecimalLimit(value ?? '')
    setValidatedValue(formattedValue)
  }, [value, validatedValue, formatValueWithDecimalLimit])

  const onValidateAmountTextInput = useCallback(
    (valueTextInput: string): string | undefined => {
      // Remove all non-numeric characters except dots, replace commas with dots
      let newValueTextInput = valueTextInput
        .replace(/,/g, '.')
        .replace(/[^0-9.]/g, '')

      // Handle dot at the beginning
      if (newValueTextInput === '.' && newValueTextInput.length === 1) {
        newValueTextInput = '0.'
      }

      // Check if input is only zeros
      const hasJustZero = newValueTextInput
        .split('')
        .every((char) => char === '0')

      // Handle empty or only zeros
      if (newValueTextInput === '' || hasJustZero) {
        newValueTextInput = '0'
      }

      // Handle leading zero replacement (e.g., "05" -> "5", but keep "0.5")
      if (
        newValueTextInput.length > 1 &&
        newValueTextInput.startsWith('0') &&
        !newValueTextInput.includes('.')
      ) {
        newValueTextInput = newValueTextInput.slice(1)
      }

      // Handle multiple dots (keep only first one)
      if (newValueTextInput.split('.').length > 2) {
        newValueTextInput = newValueTextInput.slice(0, -1)
      }

      const hasDecimals = newValueTextInput.includes('.')

      // Get maximum decimal places for this currency
      const maxDecimals = extractDynamicDecimalPlaces({
        currencyCode,
        shouldMaskStablecoinAsFiat,
      })

      // Limit decimal places
      if (hasDecimals) {
        const decimalPart = newValueTextInput.split('.')[1] || ''
        if (decimalPart.length > maxDecimals) {
          const [integerPart, decimalPart] = newValueTextInput.split('.')
          return `${integerPart}.${decimalPart.slice(0, maxDecimals)}`
        }
      }

      // Limit maxLength if exists
      if (
        typeof maxLength === 'number' &&
        newValueTextInput.length > maxLength &&
        newValueTextInput.length > validatedValue.length
      ) {
        return // block entry
      }

      setValidatedValue(newValueTextInput)

      // Handle trailing dot case
      if (newValueTextInput.endsWith('.')) {
        onChangeText?.(newValueTextInput.split('.')[0])
      }

      onChangeText?.(newValueTextInput)

      return newValueTextInput
    },
    [
      currencyCode,
      shouldMaskStablecoinAsFiat,
      maxLength,
      validatedValue.length,
      onChangeText,
    ],
  )

  return { validatedValue, onValidateAmountTextInput }
}
